/**
 * FDDiagDialog — Modal que muestra el JSON crudo del endpoint
 * `GET /clientes/{id}/sync-fd/diag` para que el admin pueda ver exactamente
 * qué le devuelve FacturaDirecta sin abrir DevTools.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Copy, ExternalLink, AlertTriangle, CheckCircle2 } from "lucide-react";

export default function FDDiagDialog({ open, onOpenChange, clienteId, clienteNombre, onSyncOk }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const load = async () => {
    if (!clienteId) return;
    setLoading(true);
    setData(null);
    try {
      const { data } = await api.get(`/clientes/${clienteId}/sync-fd/diag`);
      setData(data);
      // Si la simulación tuvo éxito y guardó fd_id, avisamos
      if (data?.simulacion_resultado?.action && data.simulacion_resultado.action !== "error" && data.cliente?.fd_id_nuevo) {
        toast.success(`✓ FD aceptó el contacto. fd_id=${data.cliente.fd_id_nuevo.slice(0, 16)}…`);
        onSyncOk?.();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error en diagnóstico FD", { duration: 10000 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && clienteId) load();
    // eslint-disable-next-line
  }, [open, clienteId]);

  const copiar = async () => {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      toast.success("JSON copiado al portapapeles");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const sim = data?.simulacion_resultado || {};
  const ok = sim.action && sim.action !== "error";
  const respuestaTxt = typeof sim.respuesta === "string"
    ? sim.respuesta
    : JSON.stringify(sim.respuesta || {}, null, 2);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="fd-diag-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            🔬 Diagnóstico FacturaDirecta — <span className="text-amber-300 truncate">{clienteNombre}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-3 py-2">
          {loading && (
            <div className="text-center py-8 text-zinc-500 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Llamando a FacturaDirecta…
            </div>
          )}

          {!loading && data && (
            <>
              {/* Resumen rápido */}
              <div className={`rounded-lg p-3 border ${ok
                ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-100"
                : "bg-rose-500/10 border-rose-500/30 text-rose-100"}`}>
                <div className="flex items-center gap-2 font-semibold">
                  {ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                  {ok ? "FD aceptó el contacto" : "FD rechazó el contacto"}
                </div>
                <div className="text-xs mt-1 opacity-90">
                  Acción: <b>{sim.action || "—"}</b>
                  {data.cliente?.fd_id_nuevo && (
                    <span> · fd_id: <code className="font-mono">{data.cliente.fd_id_nuevo}</code></span>
                  )}
                </div>
              </div>

              {/* Bloques clave */}
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-zinc-900/40 border border-zinc-800 rounded p-2.5">
                  <div className="text-zinc-500 uppercase text-[10px] tracking-wider mb-1">CIF detectado</div>
                  <div className="font-mono">{data.has_cif ? (data.payload_a_enviar?.content?.main?.fiscalId || "—") : "❌ sin CIF"}</div>
                </div>
                <div className="bg-zinc-900/40 border border-zinc-800 rounded p-2.5">
                  <div className="text-zinc-500 uppercase text-[10px] tracking-wider mb-1">Contacto ya en FD</div>
                  <div className="font-mono truncate">{data.busqueda_existente_en_fd || "(no existe)"}</div>
                </div>
              </div>

              {/* Respuesta cruda */}
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Respuesta de FacturaDirecta</div>
                <pre className="bg-black border border-zinc-800 rounded p-3 text-[10px] text-emerald-300 overflow-x-auto max-h-72 whitespace-pre-wrap break-all">
{respuestaTxt}
                </pre>
              </div>

              {/* Payload */}
              <details>
                <summary className="text-[10px] uppercase tracking-wider text-zinc-500 cursor-pointer hover:text-amber-300">
                  Payload enviado a FD
                </summary>
                <pre className="bg-black border border-zinc-800 rounded p-3 text-[10px] text-cyan-300 overflow-x-auto max-h-72 whitespace-pre-wrap mt-1">
{JSON.stringify(data.payload_a_enviar, null, 2)}
                </pre>
              </details>
            </>
          )}
        </div>

        <DialogFooter className="border-t border-zinc-800 pt-3 flex gap-2 flex-wrap">
          {data && (
            <Button variant="outline" onClick={copiar}
              data-testid="fd-diag-copiar"
              className="bg-transparent border-zinc-700 text-zinc-200">
              <Copy className="w-3.5 h-3.5 mr-1" /> Copiar JSON
            </Button>
          )}
          <a
            href="https://app.facturadirecta.com/" target="_blank" rel="noreferrer"
            className="text-xs text-cyan-300 hover:text-cyan-200 flex items-center gap-1 px-3 py-1.5"
          >
            <ExternalLink className="w-3 h-3" /> Abrir FacturaDirecta
          </a>
          <Button onClick={() => onOpenChange(false)}
            data-testid="fd-diag-cerrar"
            className="bg-amber-500 hover:bg-amber-400 text-zinc-900 ml-auto">
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
